import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { device } from '../../constants/device-sizes';
import { API_URL } from '../../constants/string-constants';

// Import shared components
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernButton,
  ModernButtonSecondary,
  FeatureList,
  FeatureItem,
  ButtonContainer,
  CloseButton,
} from './shared/ModalBase';
import {
  FieldSection,
  FieldLabel,
  FieldInput,
  FieldSelect,
} from './shared/FormComponents';

const Description = styled.p`
  color: var(--clr-sec);
  font-size: 1rem;
  text-align: center;
  margin: 1rem 0 1.5rem 0;
  line-height: 1.5;
  opacity: 0.9;

  @media ${device.tablet} {
    font-size: 1.1rem;
  }
`;

const ColorInput = styled(FieldInput)`
  height: 60px;
  cursor: pointer;

  &[type='color'] {
    padding: 4px;
    background: var(--clr-bg);
  }
`;

const FontSelect = styled(FieldSelect)`
  option[value='Inter'] {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }

  option[value='Caveat'] {
    font-family: 'Caveat', cursive;
  }

  option[value='JetBrains Mono'] {
    font-family: 'JetBrains Mono', 'Courier New', monospace;
  }

  option[value='Open Sans'] {
    font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  }

  option[value='Times New Roman'] {
    font-family: 'Times New Roman', Georgia, serif;
  }

  option[value='Inter Bold Small-Caps'] {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-variant: small-caps;
    font-weight: bold;
  }
`;

const ColorFontModal = ({
  visible,
  onClose,
  openAccountNumber,
  openAccountSignIn,
}) => {
  const [userStatus, setUserStatus] = useState({
    hasAccountNumber: false,
    hasSubscription: false,
  });
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState('#ff6b9d');
  const [font, setFont] = useState('Inter');
  const [saving, setSaving] = useState(false);

  const fontOptions = [
    { value: 'Inter', label: 'Inter (Default)' },
    { value: 'Roboto', label: 'Roboto (Clean)' },
    { value: 'Caveat', label: 'Caveat (Handwritten)' },
    { value: 'JetBrains Mono', label: 'JetBrains Mono (Code)' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Inter Bold Small-Caps', label: 'Bold Small-Caps' },
  ];

  useEffect(() => {
    if (visible) {
      checkUserStatus();
    }
  }, [visible]);

  const checkUserStatus = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      if (!auth.currentUser) {
        setUserStatus({ hasAccountNumber: false, hasSubscription: false });
        // For non-authenticated users, keep current hardcoded defaults
        setLoading(false);
        return;
      }

      const token = await auth.currentUser.getIdToken();

      // Get user status (subscription, account number)
      const statusResponse = await fetch(API_URL + 'users/status', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (statusResponse.ok) {
        const status = await statusResponse.json();
        setUserStatus(status);
        
        // Use customization settings from status response
        setColor(status.custom_color || '#ff6b9d');
        setFont(status.custom_font || 'Inter');
      }
    } catch (error) {
      console.error('Error checking user status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountNumberGeneration = () => {
    onClose();
    openAccountNumber();
  };

  const handleAccountSignIn = () => {
    onClose();
    openAccountSignIn();
  };

  const handleSubscribe = async () => {
    try {
      const auth = getAuth();

      if (!auth.currentUser) {
        alert('Please sign in to subscribe');
        return;
      }

      const token = await auth.currentUser.getIdToken();

      // Create a form and submit it to avoid fetch redirect issues
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = API_URL + 'create-checkout-session';

      // Add lookup key
      const lookupKeyInput = document.createElement('input');
      lookupKeyInput.type = 'hidden';
      lookupKeyInput.name = 'lookup_key';
      lookupKeyInput.value = 'VentScape-Monthly';
      form.appendChild(lookupKeyInput);

      // Add auth token
      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'auth_token';
      tokenInput.value = token;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(API_URL + 'users/customize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          custom_color: color,
          custom_font: font,
        }),
      });

      if (response.ok) {
        const analytics = getAnalytics();
        logEvent(analytics, 'customization_saved');
        onClose();
      } else {
        alert('Failed to save customization settings');
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      alert('Failed to save customization settings');
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <Description>Loading...</Description>;
    }

    if (!userStatus.hasAccountNumber) {
      return (
        <>
          <ModernTitle>Account Number Required</ModernTitle>
          <Description>
            Setting custom color and font is only available for subscribers.
            You'll need an account number to subscribe and access these
            features.
          </Description>
          <ButtonContainer>
            <ModernButton onClick={handleAccountNumberGeneration}>
              Generate New Account Number
            </ModernButton>
          </ButtonContainer>
          <ButtonContainer style={{ marginTop: '0.75rem' }}>
            <ModernButtonSecondary onClick={handleAccountSignIn}>
              Sign in with existing account number
            </ModernButtonSecondary>
          </ButtonContainer>
        </>
      );
    }

    if (!userStatus.hasSubscription) {
      return (
        <>
          <ModernTitle>Subscription Required</ModernTitle>
          <Description>
            Setting custom color and font is only available for subscribers.
          </Description>
          <FeatureList>
            <FeatureItem>Support the site</FeatureItem>
            <FeatureItem>
              Get access to bonus features like custom color and font
            </FeatureItem>
            <FeatureItem>
              React to messages with any emoji you want! 🙌🎉🎊
            </FeatureItem>
            <FeatureItem>
              Message length limit increased 50%! From 200 to 300 characters
            </FeatureItem>
            <FeatureItem>
              Only $4.99/month, the price of a cup of coffee ☕
            </FeatureItem>
          </FeatureList>
          <ButtonContainer>
            <ModernButton onClick={handleSubscribe}>
              Subscribe to VentScape Plus ✨
            </ModernButton>
          </ButtonContainer>
        </>
      );
    }

    return (
      <>
        <ModernTitle>Customize Your Style</ModernTitle>
        <Description>
          Choose your custom color and font for messages.
        </Description>

        <FieldSection>
          <FieldLabel>Message Color</FieldLabel>
          <ColorInput
            type='color'
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </FieldSection>

        <FieldSection>
          <FieldLabel>Message Font</FieldLabel>
          <FontSelect value={font} onChange={(e) => setFont(e.target.value)}>
            {fontOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FontSelect>
        </FieldSection>

        <ButtonContainer>
          <ModernButtonSecondary onClick={onClose}>
            Cancel
          </ModernButtonSecondary>
          <ModernButton onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes ✨'}
          </ModernButton>
        </ButtonContainer>
      </>
    );
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: {
          zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
        },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose}>
            <Times size='1.5rem' />
          </CloseButton>
          {renderContent()}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default ColorFontModal;
